import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const TouristSpending = () => {
  // State variables for tourist spending calculation
  const [totalRevenue, setTotalRevenue] = useState(0); // Total revenue from tourists
  const [numTourists, setNumTourists] = useState(0); // Number of tourists
  const [averageSpending, setAverageSpending] = useState(0); // Calculated average spending per tourist

  // Event handlers for input changes
  const handleTotalRevenueChange = (e) =>
    setTotalRevenue(parseFloat(e.target.value) || 0);
  const handleNumTouristsChange = (e) =>
    setNumTourists(parseInt(e.target.value) || 0);

  // useEffect to calculate average spending per tourist
  useEffect(() => {
    if (numTourists > 0) {
      const spending = totalRevenue / numTourists; // Calculating average spending
      setAverageSpending(spending);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [totalRevenue, numTourists]);

  return (
    <Layout title="Tourist Spending Calculator" path="tourist-spending">
      <section className="section-padding" id="tourist-spending">
        <div className="container mt-5">
          <div className="row">
            {/* Tourist Spending Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Tourist Spending Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Total Revenue */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalRevenue" className="form-label">
                        Total Revenue (in your currency)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Total Revenue:</strong> The total revenue generated from all tourists during a specific period, in your local currency."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalRevenue"
                        placeholder="Enter total revenue"
                        value={totalRevenue || ""}
                        onChange={handleTotalRevenueChange}
                      />
                    </div>

                    {/* Input for Number of Tourists */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="numTourists" className="form-label">
                        Number of Tourists
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Number of Tourists:</strong> The total number of tourists that visited during the given period."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="numTourists"
                        placeholder="Enter number of tourists"
                        value={numTourists || ""}
                        onChange={handleNumTouristsChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Average Spending per Tourist:{" "}
                        <span className="fw-bold ms-3">
                          {averageSpending === 0
                            ? "0"
                            : averageSpending.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Tourist Spending Calculation
                </h3>

                {/* What is Tourist Spending? */}
                <h4 className="mt-3 mb-3">What is Tourist Spending?</h4>
                <p>
                  **Tourist Spending** refers to the amount of money tourists
                  spend during their visit to a destination, which includes
                  expenses like accommodation, food, transportation, activities,
                  and souvenirs.
                </p>

                {/* Why Use the Tourist Spending Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use the Tourist Spending Calculator?
                </h4>
                <p>
                  This calculator helps tourism boards, travel agencies, and
                  local businesses assess the average amount tourists spend,
                  which can inform marketing strategies, economic impact
                  analysis, and pricing decisions.
                </p>

                {/* Benefits of Using the Tourist Spending Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using the Tourist Spending Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Economic Impact Analysis:</strong> Helps estimate
                    the contribution of tourism to the local economy.
                  </li>
                  <li>
                    <strong>Marketing Strategy Optimization:</strong> Allows
                    businesses to target the right demographics based on
                    spending patterns.
                  </li>
                  <li>
                    <strong>Budget Planning:</strong> Assists local businesses
                    in adjusting their offerings to meet tourist demand.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use the Tourist Spending Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Tourism Boards:</strong> To evaluate tourism-related
                    revenue and plan for future campaigns.
                  </li>
                  <li>
                    <strong>Travel Agencies:</strong> To estimate tourist
                    expenditure and create tailored packages.
                  </li>
                  <li>
                    <strong>Local Businesses:</strong> To adjust pricing,
                    inventory, and services based on tourist spending behavior.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h5 className="mt-3 mb-2">
                  Why is tourist spending important for local economies?
                </h5>
                <p>
                  Tourist spending contributes significantly to the local
                  economy, driving revenue for businesses, increasing employment
                  opportunities, and supporting infrastructure development.
                </p>

                <h5 className="mt-3 mb-2">
                  How can businesses increase tourist spending?
                </h5>
                <p>
                  By offering attractive packages, promoting local experiences,
                  and improving customer service, businesses can enhance tourist
                  satisfaction and encourage higher spending.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TouristSpending;
